import type { FormEvent } from "react";

import { Button, Text, toastError } from "@alpacahq/ui/old";

import {
  useGetAccountDocumentUploads,
  useMutationRequest,
} from "~/v2/api/hooks";
import { FILE_MAX_SIZE, FileDropzone } from "~/v2/components/fileUpload";
import {
  type Account,
  type DocUploadData,
  uploadAccountDocumentByDocType,
} from "../../../../../api/rest/account";

type TrulioProps = { account: Account; handleCompleted: () => void };

const Trulioo = ({ account, handleCompleted }: TrulioProps) => {
  const { data: uploads, refetch } = useGetAccountDocumentUploads({
    accountId: account.id,
  });
  const { mutate } = useMutationRequest(
    (payload: DocUploadData) =>
      uploadAccountDocumentByDocType(account?.id ?? "", payload),
    {
      onSuccess: () => {
        refetch();
      },
      onError: (error) => {
        toastError(`Error: ${error.message}`);
      },
    },
  );

  const upload = (file: File) => {
    mutate({
      doc_type: "identity_verification",
      doc_sub_type: "drivers_license",
      content: file,
    });
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    handleCompleted();
  };

  const uploadedFile = uploads?.find(
    (upload) => upload.doc_sub_type === "drivers_license",
  );

  const uploaded =
    uploads?.filter((v) => v.doc_sub_type === "drivers_license").length || 0;

  const complete = uploaded > 0;

  return (
    <div className="mb-16">
      <form onSubmit={onSubmit}>
        <FileDropzone
          isRequired
          label="Identity Verification"
          placeholder=""
          maxSize={FILE_MAX_SIZE}
          accept={["image/png", "image/jpeg", "image/webp"]}
          name="identity_verification"
          file={
            uploadedFile
              ? { id: uploadedFile.id, name: uploadedFile.doc_sub_type }
              : null
          }
          onUpload={(file) => upload(file)}
          onError={(rejection) =>
            toastError(`Error: ${rejection.errors.at(0)?.message}`)
          }
        />
        <div className="my-8 flex flex-col gap-4">
          {uploaded > 0 && <p>You have uploaded {uploaded} documents.</p>}
          <Text className="text-red-400" color="red">
            You must upload a valid form of verification such as a driver&apos;s
            license, a passport, an identity card or a residence permit to
            ensure your application can be approved.
          </Text>
          <Text className="w-full">
            Please ensure that the picture you attach shows all four edges of
            the document.
          </Text>
        </div>
        <Button
          className="w-full"
          type="submit"
          disabled={!complete}
          amplitude-event-name="onbv2_domestic_page_3_kyc_completed"
        >
          Continue
        </Button>
      </form>
    </div>
  );
};

export default Trulioo;
